import { useState, useEffect } from 'react'
import { Search, X, Sparkles } from 'lucide-react'

interface SearchBarProps {
  onSearch: (query: string, embeddingsMode: boolean) => void
  initialQuery?: string
  initialEmbeddings?: boolean
}

export default function SearchBar({ onSearch, initialQuery = '', initialEmbeddings = false }: SearchBarProps) {
  const [query, setQuery] = useState(initialQuery)
  const [embeddingsMode, setEmbeddingsMode] = useState(initialEmbeddings)

  // Sync with external changes to initialQuery
  useEffect(() => {
    setQuery(initialQuery)
  }, [initialQuery])

  useEffect(() => {
    setEmbeddingsMode(initialEmbeddings)
  }, [initialEmbeddings])

  function handleSearch() {
    const trimmed = query.trim()
    if (!trimmed) return
    onSearch(trimmed, embeddingsMode)
  }

  function handleKeyDown(e: React.KeyboardEvent) {
    if (e.key === 'Enter') {
      handleSearch()
    }
  }

  function handleClear() {
    setQuery('')
  }

  return (
    <div className="flex items-center gap-3 w-full">
      {/* Search input */}
      <div className="relative flex-1">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
        <input
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="Search journals, resources, topics..."
          className="w-full pl-9 pr-8 py-2 border border-gray-300 rounded-lg text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-acad-500 focus:border-acad-500 transition"
        />
        {query && (
          <button
            onClick={handleClear}
            className="absolute right-2 top-1/2 -translate-y-1/2 p-0.5 text-gray-400 hover:text-gray-600 transition"
            aria-label="Clear search"
          >
            <X className="w-4 h-4" />
          </button>
        )}
      </div>

      {/* Embeddings toggle */}
      <label className="flex items-center gap-1.5 cursor-pointer select-none flex-shrink-0">
        <input
          type="checkbox"
          checked={embeddingsMode}
          onChange={(e) => setEmbeddingsMode(e.target.checked)}
          className="sr-only peer"
        />
        <div className="relative w-8 h-[18px] bg-gray-200 peer-checked:bg-violet-500 rounded-full transition-colors">
          <div className="absolute left-0.5 top-0.5 w-3.5 h-3.5 bg-white rounded-full shadow-sm transition-transform peer-checked:translate-x-3.5" />
        </div>
        <Sparkles className="w-3.5 h-3.5 text-violet-500" />
        <span className="text-xs text-gray-600 hidden sm:inline">AI Search</span>
      </label>

      {/* Search button */}
      <button
        onClick={handleSearch}
        className="px-4 py-2 bg-acad-500 hover:bg-acad-600 active:bg-acad-700 text-white text-sm font-medium rounded-lg transition flex-shrink-0"
      >
        Search
      </button>
    </div>
  )
}
